/**
 * StitchPanelSection — the stitch-line properties cluster extracted from
 * PropertiesPanel (#865, epic #828 slice PP-S1). PropertiesPanel OWNS a
 * StitchPanelSection instance and DRIVES it by composition (it is NOT a base
 * class), mirroring the Scene renderer precedents (PieceRenderer /
 * ConstructionHoverController): the section holds the stitch-seam properties
 * BUILDERS (`appendStitchPanel` and its sub-builders — the match / grid /
 * follower-skip sections, the inline stitch-name link, the follower summary,
 * and the shared stitching-defaults editor), while the `render()` dispatch that
 * decides WHEN to call them stays on PropertiesPanel.
 *
 * Everything the builders read from the owning panel (the DOM append target,
 * the editor context, the panel refresh, the shared stitch commit + working-
 * area / promote / connection-row builders, the "armed line" transient, and the
 * stitch-match arming bridge) is injected through the `StitchPanelHost`
 * interface. The shared form builders (`textField` / `lengthField` / `checkbox`
 * / the stitch galleries / `ordinal` / …) stay single-definition on
 * PropertiesPanel and are imported here. Behaviour is identical to the in-panel
 * original — this is a pure move + host rewiring.
 */
import type { EditorContext } from "../tools/EditorContext";
import { markPaidFeature, markPaidSection } from "./paidPreview";
import type {
  Piece,
  Project,
  StitchingDefaults,
  StitchLine,
  StitchRef,
} from "../model/Project";
import {
  pieceForStitchLine,
  pieceStitchLines,
  resolvePartStitchingDefaults,
  resolveProjectStitchingDefaults,
} from "../model/Project";
import {
  defaultPitchForShape,
  defaultPitchModeForShape,
  defaultToothWidthForPitch,
  isAutoToothForPitch,
  setPartStitchingDefaults,
  setProjectStitchingDefaults,
  stitchSizeRange,
  effectiveStitchSpacing,
  effectiveHoleCount,
  effectiveSkipEvery,
  maxSkipEvery,
  defaultSkipEvery,
  isStitchLoop,
} from "../model/stitching";
import { placedStitchHoles } from "../model/placedStitchHoles";
import {
  matchForStitch,
  matchesForStitch,
  stitchMatchPartner,
  isStitchMatchMain,
  breakStitchMatch,
  matchFollowersToMain,
} from "../model/stitchMatch";
import { stitchGridGroupFor } from "../model/constructionStitchGrid";
import { promoteStitchToConstruction } from "../model/promoteToConstruction";
import type { ConnectionRowSpec } from "../model/connections";
import { subTraceRowSpecs } from "../model/connections/traceConnection";
import {
  stitchGridSpecBase,
  stitchMatchLabel,
  stitchMatchSpecBase,
} from "../model/connections/stitchSpecs";
import { isFeatureEnabled } from "../featureFlags";
import {
  advancedStitchLayoutEnabled,
  effectiveCornerAngle,
  effectiveMinGapFraction,
  MIN_GAP_FRACTION,
  usesCornerLayout,
  CORNER_ANGLE_MIN_DEG,
  CORNER_ANGLE_MAX_DEG,
  CORNER_MAX_SMOOTH_DEG,
} from "../model/stitchLayout";
import { SKIP_STITCH_TECHNIQUE_URL } from "../instructions/stepExtractor";
import { ordinal, bucketmateLineIds } from "./PropertiesPanel";
import type { StitchingScope } from "../canvas/Scene";
import {
  textField,
  lengthField,
  checkbox,
  stitchSummary,
  stitchShapeGallery,
  stitchPitchField,
  directionToggle,
  slantDirectionToggle,
  numberField,
  scrubberInputField,
  panelSection,
  fieldNote,
  RESMOOTH_DISCARDS_EDITS_NOTE,
} from "./panelFieldBuilders";
import {
  DEFAULT_LEATHER_SMOOTHNESS,
  hasFreehandSource,
} from "../model/leatherSmoothing";
import {
  smoothnessFromPercent,
  smoothnessPercent,
} from "../model/freehandSmoothing";

/** Build the shared chip + Connections-row spec for one member of a stitch
 *  match (#199). Both members share `connectionKey` so hovering either side
 *  cross-highlights the other's chip / row. Reuses the connection-chip UI. */
function stitchMatchRowSpec(
  matchId: string,
  partner: StitchRef,
  partnerName: string,
  isMain: boolean,
): ConnectionRowSpec {
  // A row IS a chip plus `label` + the detach commit, and the chip half comes
  // from the shared spec source (#984) — so the key, role, icon, wording, and
  // the partner `clickTarget` are the same object the layers chip renders. They
  // drifted before: the chip selected the partner, the row didn't (#979).
  return {
    ...stitchMatchSpecBase(matchId, partner, partnerName, isMain),
    label: stitchMatchLabel(partnerName, isMain),
    onDetach: (ctx) =>
      ctx.commit("Break stitch match", (proj) =>
        breakStitchMatch(proj, matchId),
      ),
  };
}

/** Build a read-only chip + Connections-row spec for one side of the
 *  construction-stitch grid (#199 Phase 3). All members of a grid group share
 *  `connectionKey` (keyed on the leader) so hovering any row / chip cross-
 *  highlights the rest. No `onDetach` — the grid is emergent (change a seam's
 *  pitch or detach it from construction to leave it), so the row is read-only. */
function stitchGridRowSpec(
  leaderStitchId: string,
  partner: StitchRef | null,
  label: string,
  isLeader: boolean,
): ConnectionRowSpec {
  // Shared spec source with the layers CHIP (#984): same key / role / icon /
  // class / partner `clickTarget`; only the wording differs, since a leader
  // shows one summary chip but one row per aligned seam.
  return {
    ...stitchGridSpecBase(leaderStitchId, partner, isLeader, label),
    label,
  };
}

/** The shared PropertiesPanel state + services the stitch builders reach back
 *  into. The DOM append target, the editor context, and the shared commit /
 *  working-area / promote / connection-row builders stay owned by the panel
 *  (non-stitch code uses them too); the "armed line" transient stays a single
 *  field on the panel because `refresh()` also clears it. Implemented by
 *  PropertiesPanel. */
export interface StitchPanelHost {
  /** The panel's root append target — `appendStitchPanel`'s final append. */
  getRoot(): HTMLElement;
  /** The shared editor context (history / commit / scene / selection). */
  getCtx(): EditorContext;
  /** Rebuild the properties panel DOM for the current selection. */
  refresh(): void;
  /** Commit an undoable edit to one stitch line (shared with the working-area
   *  field that stays on the panel). */
  updateStitch(
    pieceId: string,
    stitchId: string,
    fn: (l: StitchLine) => StitchLine,
  ): void;
  /** The dual-thumb "working area" range field — stays on the panel because it
   *  reaches the panel-private preview helper. */
  appendWorkingAreaField(
    wrap: HTMLElement,
    pieceId: string,
    line: StitchLine,
    onInsets?: (startInset: number, endInset: number) => void,
    includeStartOfPath?: boolean,
  ): void;
  /** The "promote to construction" button builder (shared with the part /
   *  cutout panels). */
  promoteButton(what: string, onClick: () => void): HTMLButtonElement;
  /** One Connections-section row (shared with the construction / part panels). */
  buildConnectionRow(spec: ConnectionRowSpec): HTMLLIElement;
  /** The id of a stitch line the user has "armed" for an override preview — a
   *  panel field shared with `refresh()`, which clears it when the selection
   *  leaves the line. */
  getArmedLineId(): string | null;
  setArmedLineId(id: string | null): void;
  /** Arm the "pick a partner stitch" flow (#199) — a no-op until app.ts wires
   *  the handler. */
  armStitchMatch(source: StitchRef): void;
}

/** The default shortest-gap floor as the whole percentage the panel shows, so
 *  the "is this still the default?" test is a comparison of the same units the
 *  control works in rather than a float dance. */
const DEFAULT_MIN_GAP_PCT = Math.round(MIN_GAP_FRACTION * 100);

export class StitchPanelSection {
  constructor(private host: StitchPanelHost) {}

  /** The project as it stood when a corner-angle drag began, so the release can
   *  rewind before committing and leave ONE undo step for the whole sweep.
   *
   *  On the instance, not in the field's closure: every live frame calls
   *  `history.replace`, which re-renders the properties panel and hands the
   *  next frame a brand-new closure. A closure-local origin would therefore be
   *  re-captured from the already-previewed state on every tick, and the rewind
   *  would land on the last preview instead of on what the user had before they
   *  touched the scrubber. Keyed by seam so a drag abandoned by selecting
   *  something else can't rewind a later one. */
  private scrubberPreDrag: { key: string; project: Project } | null = null;

  /** The editor context is owned by PropertiesPanel and reached through the
   *  host; the stitch builders read history / commit / scene / selection off it. */
  private get ctx(): EditorContext {
    return this.host.getCtx();
  }
  /** The panel's DOM append target, reached through the host. */
  private get root(): HTMLElement {
    return this.host.getRoot();
  }
  /** The "armed line" transient lives on the panel (shared with `refresh()`);
   *  the section reads / writes it through the host so the moved bodies stay
   *  byte-identical. */
  private get armedLineId(): string | null {
    return this.host.getArmedLineId();
  }
  private set armedLineId(id: string | null) {
    this.host.setArmedLineId(id);
  }
  /** Thin delegators so the moved bodies keep calling `this.refresh()` /
   *  `this.updateStitch(...)` / … unchanged; the real implementations stay
   *  single-definition on the panel. */
  private refresh(): void {
    this.host.refresh();
  }
  private updateStitch(
    pieceId: string,
    stitchId: string,
    fn: (l: StitchLine) => StitchLine,
  ): void {
    this.host.updateStitch(pieceId, stitchId, fn);
  }
  private appendWorkingAreaField(
    wrap: HTMLElement,
    pieceId: string,
    line: StitchLine,
    onInsets?: (startInset: number, endInset: number) => void,
    includeStartOfPath = true,
  ): void {
    this.host.appendWorkingAreaField(
      wrap,
      pieceId,
      line,
      onInsets,
      includeStartOfPath,
    );
  }
  private promoteButton(what: string, onClick: () => void): HTMLButtonElement {
    return this.host.promoteButton(what, onClick);
  }
  private buildConnectionRow(spec: ConnectionRowSpec): HTMLLIElement {
    return this.host.buildConnectionRow(spec);
  }

  /** The human-facing hole count the panel shows for a seam (#910 PR3): how
   *  many holes actually land ON the piece's leather — the work-area window
   *  count MINUS the holes that overflow the outline / fall in a cutout — so a
   *  seam spilling past its piece reports FEWER holes, matching what render /
   *  export / instructions show. Routes through the shared `placedStitchHoles`
   *  retention (the same paper scope render / exporters use, reached through the
   *  scene). This is deliberately NOT `effectiveHoleCount`, which stays the
   *  paper-free pre-outline-filter count for match-seeding / ring-math. Falls
   *  back to the along-seam count when the owning piece can't be resolved. */
  private retainedHoleCount(line: StitchLine, pieceId: string): number {
    const piece = this.ctx.history.state.pieces.find((p) => p.id === pieceId);
    if (!piece) return effectiveHoleCount(line);
    return placedStitchHoles(line, piece, this.ctx.scene.getPaperScope())
      .length;
  }

  /**
   * The advanced-layout controls (#691): a corner-aware row, and the radius its
   * corners turn on.
   *
   * Two controls rather than three, because the third bullet of the ticket —
   * dropping a gap too short to punch — is not a decision anyone wants to make
   * per seam. It happens whenever the layout is on, which is what "automatically"
   * meant.
   *
   * Note what the copy does NOT offer: any suggestion that the spacing changes.
   * The pitch is the iron's, and the layout only moves the row along the seam.
   * See `src/model/stitchLayout.ts`.
   */
  /** The project with one seam's corner angle changed — the shape of an edit,
   *  without committing it, so the live scrubber can hand it to
   *  `history.replace` and the release can hand the same change to `commit`. */
  private withStitchPatch(
    pieceId: string,
    stitchId: string,
    patch: Partial<StitchLine>,
  ) {
    const proj = this.ctx.history.state;
    return {
      ...proj,
      pieces: proj.pieces.map((pc) =>
        pc.id !== pieceId
          ? pc
          : {
              ...pc,
              stitchLines: pieceStitchLines(pc).map((l) =>
                l.id === stitchId ? { ...l, ...patch } : l,
              ),
            },
      ),
    };
  }

  /**
   * A scrubber whose drag PREVIEWS live and commits once on release (#691).
   *
   * `history.replace` swaps the state and repaints without stacking an undo
   * entry, so a sweep across the whole range doesn't bury the user's previous
   * edit under a hundred of them; the release rewinds to where the drag began
   * and then commits, leaving exactly one step.
   *
   * The pre-drag project is held on the INSTANCE, not in this closure: every
   * live frame re-renders the panel and hands the next frame a fresh closure,
   * so a closure-local origin would be re-captured from the already-previewed
   * state on every tick and the rewind would land on the last preview.
   */
  private livePatchScrubber(
    pieceId: string,
    line: StitchLine,
    opts: {
      label: string;
      min: number;
      max: number;
      /** Omit for whole numbers, which is what `scrubberInputField` defaults to. */
      step?: number;
      value: number;
      suffix?: string;
      ariaLabel?: string;
      /** The edit this value means — `undefined` to store nothing (default). */
      patch: (v: number) => Partial<StitchLine>;
    },
  ): HTMLElement {
    return scrubberInputField({
      label: opts.label,
      min: opts.min,
      max: opts.max,
      step: opts.step,
      value: opts.value,
      suffix: opts.suffix,
      ariaLabel: opts.ariaLabel,
      onLive: (v: number) => {
        if (this.scrubberPreDrag?.key !== `${line.id}:${opts.label}`) {
          this.scrubberPreDrag = {
            key: `${line.id}:${opts.label}`,
            project: this.ctx.history.state,
          };
        }
        this.ctx.history.replace(
          this.withStitchPatch(pieceId, line.id, opts.patch(v)),
        );
      },
      onCommit: (v: number) => {
        if (this.scrubberPreDrag?.key === `${line.id}:${opts.label}`) {
          this.ctx.history.replace(this.scrubberPreDrag.project);
        }
        this.scrubberPreDrag = null;
        this.updateStitch(pieceId, line.id, (l) => ({
          ...l,
          ...opts.patch(v),
        }));
      },
    }).wrap;
  }

  private appendCornerLayoutFields(
    wrap: HTMLElement,
    pieceId: string,
    line: StitchLine,
  ): void {
    if (!advancedStitchLayoutEnabled()) return;
    // A matched follower / main lays its row out by COUNT (#199), so the
    // corner-aware pitch layout has nothing to act on.
    if (line.holeCount != null && line.holeCount >= 1) return;
    // A construction-grid follower punches its leader's holes (#1105), so it
    // has no layout of its own to control.
    if (line.gridPhase != null) return;
    // Short label: the long version of the explanation now goes to the maker
    // who needs it, in the build instructions for punching this seam, rather
    // than sitting in the panel of someone who has already chosen it.
    const on = usesCornerLayout(line);
    const advanced = panelSection("Advanced stitch control");
    markPaidSection(advanced, "stitch-layout-algorithms");
    wrap.appendChild(advanced);
    advanced.appendChild(
      checkbox("Even spacing", on, (v) => {
        this.updateStitch(pieceId, line.id, (l) => ({
          ...l,
          cornerLayout: v ? true : undefined,
          // Switching the layout off drops the pivot-hole opt-out with it, so
          // turning it back on starts from the default (round) rather than from
          // a choice made in some earlier session about holes that no longer
          // existed.
          ...(v
            ? {}
            : { roundCornerHoles: undefined, noEndSpacing: undefined }),
        }));
        // The toggle SHOWS and HIDES the fields below it, so the panel has to
        // rebuild — `updateStitch` only commits. (Every other control here
        // edits a value in place and needs no rebuild, which is why the refresh
        // isn't inside `updateStitch`.)
        this.refresh();
      }),
    );
    if (!on) return;
    // A loop has no ends, so there is nothing there to ask about.
    if (!isStitchLoop(line)) {
      advanced.appendChild(
        checkbox(
          "Don't change stitch ends",
          line.noEndSpacing !== false,
          (v) => {
            // Only the opt-OUT is stored: a seam usually has to meet something
            // at each end, so leaving them on pitch is what you get by default.
            this.updateStitch(pieceId, line.id, (l) => ({
              ...l,
              noEndSpacing: v ? undefined : false,
            }));
          },
        ),
      );
    }
    // How short a gap the seam will punch before it drops the hole instead.
    // Governs both places a hole is dropped — a run's leftover, and a corner
    // pin crowding the one before it — because they are the same judgement:
    // two holes this close read as a double-punch rather than a tight stitch.
    // Not a constant, because it depends on the leather and the thread as much
    // as on the iron.
    advanced.appendChild(
      this.livePatchScrubber(pieceId, line, {
        label: "Shortest stitch spacing",
        min: 0,
        max: 100,
        value: Math.round(effectiveMinGapFraction(line) * 100),
        suffix: "%",
        ariaLabel:
          "Shortest stitch spacing to punch, as a percentage of the pitch",
        // Shown as a whole percentage, stored as the fraction the model works
        // in. A percentage is what a maker reads off the row — "half a stitch"
        // — where 0.66 needs translating first.
        patch: (pct) => ({
          minGapFraction: pct === DEFAULT_MIN_GAP_PCT ? undefined : pct / 100,
        }),
      }),
    );
    advanced.appendChild(
      fieldNote(
        "As a percentage of the pitch. Two holes closer than this drop one rather than being punched almost on top of each other. 100% is a full stitch; 0 punches every leftover however tight.",
      ),
    );
    // The pivot holes get their own section: what they're punched with, and
    // which bends earn one. Two settings about the same thing, so they read as
    // a group rather than as more rows under the spacing toggle.
    //
    // A SIBLING of the section above, not a child of it (#1050). Nested, it
    // drew a second full-width rule partway down a section that hadn't ended —
    // the rule spans the sidebar, so it reads as a boundary between two
    // sections wherever it lands.
    const corners = panelSection("Corner holes");
    // What they're punched WITH goes first — it's the yes/no, and the angle
    // below refines which holes it applies to. Nothing to ask on a seam already
    // punched round throughout.
    if (line.shape !== "round") {
      corners.appendChild(
        checkbox("Round corner holes", line.roundCornerHoles !== false, (v) => {
          // Only the opt-OUT is stored: round is the reason to want pivot holes,
          // so absent means round.
          this.updateStitch(pieceId, line.id, (l) => ({
            ...l,
            roundCornerHoles: v ? undefined : false,
          }));
        }),
      );
    }
    // Which bends count as corners. A judgement about the piece — a polyline
    // standing in for a curve wants a low setting or every anchor becomes a
    // pivot hole; a chamfer or a shallow gusset fold wants a high one or it is
    // walked straight past.
    corners.appendChild(
      this.livePatchScrubber(pieceId, line, {
        label: "Corner angle",
        min: CORNER_ANGLE_MIN_DEG,
        max: CORNER_ANGLE_MAX_DEG,
        step: 1,
        value: effectiveCornerAngle(line),
        suffix: "°",
        ariaLabel: "Widest angle still treated as a smooth bend",
        patch: (deg) => ({
          cornerMaxSmoothAngle: deg === CORNER_MAX_SMOOTH_DEG ? undefined : deg,
        }),
      }),
    );
    corners.appendChild(
      fieldNote(
        "Bends tighter than this get a pivot hole; wider ones are left smooth. 180° is a straight line, 90° a square corner.",
      ),
    );
    wrap.appendChild(corners);
  }

  public appendStitchPanel(pieceId: string, line: StitchLine, idx: number) {
    const wrap = document.createElement("div");
    const heading = document.createElement("h3");
    heading.textContent = "Stitch line";
    wrap.appendChild(heading);

    const fallbackName = `Stitch ${idx + 1}`;
    wrap.appendChild(
      textField("Name", line.name ?? fallbackName, (newName) => {
        this.updateStitch(pieceId, line.id, (l) => ({ ...l, name: newName }));
      }),
    );

    // A FOLLOWER seam (#199) is driven entirely by its main's hole count, so
    // its own spacing / chisel controls no longer apply — collapse the panel to
    // just the match info (Follows … + Break). Its stored shape / size stay
    // frozen until the match is broken. The MAIN seam keeps its full controls
    // and gets a "… follows this" note appended at the end.
    const matchHere = isFeatureEnabled("stitch-matching")
      ? matchForStitch(this.ctx.history.state, line.id)
      : null;
    if (matchHere && !isStitchMatchMain(matchHere, line.id)) {
      this.appendStitchMatchSection(wrap, pieceId, line);
      this.root.appendChild(wrap);
      return;
    }

    // Smoothness (#1016) — a TRACED seam that still carries its raw pointer
    // samples. Since #982 smoothing yields a real curve, so raising this fits
    // the trace with fewer anchors rather than thinning a polyline.
    if (hasFreehandSource(line)) {
      const lineId = line.id;
      wrap.appendChild(
        numberField(
          "Smoothness (%)",
          smoothnessPercent(line.smoothness ?? DEFAULT_LEATHER_SMOOTHNESS),
          0,
          100,
          1,
          (pct) => {
            const v = smoothnessFromPercent(pct);
            this.ctx.commit("Adjust smoothness", (proj) => ({
              ...proj,
              pieces: proj.pieces.map((pc) =>
                pc.id === pieceId
                  ? {
                      ...pc,
                      stitchLines: (pc.stitchLines ?? []).map((l) =>
                        // Applying smoothness makes the seam a derived view of
                        // the trace again, so the derive pass takes over from
                        // here (#248).
                        l.id === lineId
                          ? { ...l, smoothness: v, freehandEdited: undefined }
                          : l,
                      ),
                    }
                  : pc,
              ),
            }));
          },
        ),
      );
      if (line.freehandEdited) {
        wrap.appendChild(fieldNote(RESMOOTH_DISCARDS_EDITS_NOTE));
      }
    }

    // Legacy lines (saved before the flag existed) are treated as overriding
    // so their pre-existing stored values stay in effect.
    const overrides = line.overridesDefaults !== false;
    // A construction-grid FOLLOWER (it carries a derived `gridPhase`) takes its
    // leader's chisel as well as its holes (#1105), so it has no settings of its
    // own: no override, no editor, and "Stitch settings" opens the leader's.
    const isGridFollower = line.gridPhase != null;
    const gridLeader = isGridFollower
      ? (stitchGridGroupFor(this.ctx.history.state, line.id)?.leader ?? null)
      : null;
    const isArmed =
      !isGridFollower && !overrides && this.armedLineId === line.id;
    // Editor is only shown while "armed" (previewing the override). For an
    // already-overriding line, the chisel / pitch / size / slant controls
    // live on the bucket (group) panel — selecting the matching "Stitching
    // (...)" entry in the Layers panel opens them. This keeps each line's
    // settings managed in one place rather than two.
    const showEditor = isArmed;

    // The style status line, e.g. "Custom override · round 4 mm". When
    // armed-but-not-yet-overriding we tell the user they're previewing defaults.
    const statusText = isGridFollower
      ? stitchSummary(line)
      : overrides
        ? `Custom override · ${stitchSummary(line)}`
        : isArmed
          ? `Showing defaults · change a value to override`
          : `Following defaults · ${stitchSummary(line)}`;

    // Status + summary, with this seam's total hole count at the START of the
    // line (updated live as the working-area range is dragged).
    const summary = document.createElement("div");
    summary.className = "stitch-summary";
    const renderSummary = (l: StitchLine) => {
      const n = this.retainedHoleCount(l, pieceId);
      summary.textContent = `${n} hole${n === 1 ? "" : "s"} · ${statusText}`;
    };
    renderSummary(line);
    // Summary at the top, right under the name field (matches the follower panel).
    wrap.appendChild(summary);

    // Working-area heads: inset the row of holes from each end of the seam.
    // Always editable — it's a positioning tweak that doesn't conflict with
    // the part / project chisel defaults. A construction-grid FOLLOWER (it
    // carries a derived `gridPhase`) hides its Start-of-path control — its
    // rotation is set by the grid leader's start, not its own (#692).
    this.appendWorkingAreaField(
      wrap,
      pieceId,
      line,
      (startInset, endInset) =>
        renderSummary({ ...line, startInset, endInset }),
      !isGridFollower,
    );

    // Traversal direction (#796) — a per-line, positional property (like
    // startOffset / working area), so it commits directly without promoting the
    // line to a chisel-override. Always available for a selected seam.
    wrap.appendChild(
      directionToggle(line.direction ?? "cw", (dir) => {
        this.updateStitch(pieceId, line.id, (l) => ({ ...l, direction: dir }));
      }),
    );

    if (showEditor) {
      // Promote-on-edit: when the user is just "armed" (not yet
      // overriding), the first real change to any field flips the override
      // flag and re-focuses the selection on the bucket the line lands in.
      const promote = (apply: (l: StitchLine) => StitchLine) => {
        const wasArmed = isArmed;
        this.updateStitch(pieceId, line.id, (l) => ({
          ...apply(l),
          overridesDefaults: true,
        }));
        if (wasArmed) {
          this.armedLineId = null;
          // Find the bucket the line ended up in (it may have joined an
          // existing one) and select it so the bucket panel shows the
          // override the user just created.
          const proj = this.ctx.history.state;
          const piece = proj.pieces.find((p) => p.id === pieceId);
          if (piece) {
            const lineIds = bucketmateLineIds(piece, line.id);
            this.ctx.setSelection({
              pieceId: null,
              decorationId: null,
              stitchingScope: { kind: "bucket", pieceId, lineIds },
            });
          } else {
            this.refresh();
          }
        } else {
          this.refresh();
        }
      };

      wrap.appendChild(
        stitchShapeGallery(line.shape, (shape) => {
          // Switching chisel resets the size + pitch + pitchMode to that
          // chisel's natural defaults — the previous chisel's values
          // rarely make sense in the new context (a 1.8 mm slant tooth
          // width isn't a 1.8 mm round diameter; a 3.38 mm slant pitch
          // reads tight for round holes; diamond chisels prefer "gap"
          // measurement).
          promote((l) => ({
            ...l,
            shape,
            size: stitchSizeRange(shape).defaultValue,
            pitch: defaultPitchForShape(shape),
            pitchMode: defaultPitchModeForShape(shape),
          }));
        }),
      );

      wrap.appendChild(
        stitchPitchField(line.pitch, {
          mode: line.pitchMode ?? "center",
          onModeChange: (mode) => {
            promote((l) => ({ ...l, pitchMode: mode }));
          },
          onPitchChange: (v, modeOverride, fromPreset) => {
            promote((l) => {
              // Auto-scale the tooth width on preset clicks for slant +
              // diamond chisels (both share tooth-width semantics) — but
              // only when the user hasn't already manually set a tooth
              // value (detected by comparing the current tooth to the
              // auto value for the current pitch).
              const autoTooth =
                fromPreset &&
                (l.shape === "slant" || l.shape === "diamond") &&
                isAutoToothForPitch(l.size, l.pitch);
              return {
                ...l,
                pitch: v,
                ...(modeOverride !== undefined
                  ? { pitchMode: modeOverride }
                  : {}),
                ...(autoTooth ? { size: defaultToothWidthForPitch(v) } : {}),
              };
            });
          },
          onResetToDefaults: () => {
            // Reset every chisel parameter to the active shape's defaults
            // — keeps the chisel selection but throws away pitch / size /
            // mode / slant tweaks.
            promote((l) => ({
              ...l,
              pitch: defaultPitchForShape(l.shape),
              pitchMode: defaultPitchModeForShape(l.shape),
              size: stitchSizeRange(l.shape).defaultValue,
              slantAngle: l.shape === "slant" ? -45 : l.slantAngle,
            }));
          },
        }),
      );

      const sizeRange = stitchSizeRange(line.shape);
      wrap.appendChild(
        lengthField(
          sizeRange.label,
          line.size,
          sizeRange.min,
          sizeRange.max,
          sizeRange.step,
          (v) => {
            promote((l) => ({ ...l, size: v }));
          },
          { noSlider: true },
        ),
      );

      if (line.shape === "slant" || line.shape === "diamond") {
        wrap.appendChild(
          slantDirectionToggle(line.slantAngle, (deg) => {
            promote((l) => ({ ...l, slantAngle: deg }));
          }),
        );
      }
    }

    // The override toggle and the "Stitch settings" locator (#748) share a
    // row: toggle on the left, locator on the right.
    const btnRow = document.createElement("div");
    btnRow.style.display = "flex";
    btnRow.style.gap = "8px";
    btnRow.style.marginTop = "8px";
    const toggleBtn = document.createElement("button");
    toggleBtn.type = "button";
    toggleBtn.className = "btn";
    toggleBtn.style.flex = "1";
    toggleBtn.textContent = overrides
      ? "Stop overriding"
      : isArmed
        ? "Cancel override"
        : "Override";
    toggleBtn.addEventListener("click", () => {
      if (overrides) {
        // Resolve the line's effective (cascade) values from the part /
        // project chain and pull them back in. Drop the override flag so
        // future cascade-on-write updates flow through.
        const proj = this.ctx.history.state;
        const piece = proj.pieces.find((p) => p.id === pieceId);
        const eff = piece
          ? resolvePartStitchingDefaults(proj, piece)
          : resolveProjectStitchingDefaults(proj);
        this.updateStitch(pieceId, line.id, (l) => ({
          ...l,
          shape: eff.shape,
          pitch: eff.pitch,
          size: eff.size,
          slantAngle: eff.slantAngle,
          overridesDefaults: false,
        }));
        this.refresh();
      } else if (isArmed) {
        this.armedLineId = null;
        this.refresh();
      } else {
        // Arm — no commit yet. The editor unfolds; first edit promotes.
        this.armedLineId = line.id;
        this.refresh();
      }
    });
    if (!isGridFollower) btnRow.appendChild(toggleBtn);

    // "Stitch settings" locator (#748): jump to where this seam's chisel
    // settings actually live — the part's / project's "Stitching" row for an
    // inheriting seam (whichever level the cascade resolves from), or the
    // seam's own override bucket ("Stitching (…)") for an overriding one.
    // Selecting the scope opens its settings editor in this panel and
    // highlights (and reveals) the row in the Parts list.
    const locateBtn = document.createElement("button");
    locateBtn.type = "button";
    locateBtn.className = "btn";
    locateBtn.style.flex = "1";
    locateBtn.textContent = "Stitch settings";
    // A grid follower's settings are its leader's (#1105): the leader's own
    // override group when it has one, else the leader's part.
    const settingsOwner = (proj: Project) => {
      if (gridLeader) {
        const leadPiece = proj.pieces.find((p) => p.id === gridLeader.pieceId);
        const lead = leadPiece
          ? pieceStitchLines(leadPiece).find(
              (l) => l.id === gridLeader.stitchId,
            )
          : undefined;
        if (leadPiece && lead) return { piece: leadPiece, line: lead };
      }
      const piece = proj.pieces.find((p) => p.id === pieceId);
      return piece ? { piece, line } : null;
    };
    {
      const proj = this.ctx.history.state;
      const piece = proj.pieces.find((p) => p.id === pieceId);
      const owner = gridLeader ? settingsOwner(proj) : null;
      locateBtn.title = owner
        ? owner.line.overridesDefaults !== false
          ? "Show the stitch settings this seam follows (its grid leader's Stitching group)"
          : "Show the stitch settings this seam follows (its grid leader's part)"
        : overrides
          ? "Show this seam's override settings (its Stitching group)"
          : piece?.stitchingDefaults
            ? "Show this part's stitching defaults (what this seam follows)"
            : "Show the project's stitching defaults (what this seam follows)";
    }
    locateBtn.addEventListener("click", () => {
      const proj = this.ctx.history.state;
      const owner = gridLeader ? settingsOwner(proj) : null;
      if (owner) {
        const scope: StitchingScope =
          owner.line.overridesDefaults !== false
            ? {
                kind: "bucket",
                pieceId: owner.piece.id,
                lineIds: bucketmateLineIds(owner.piece, owner.line.id),
              }
            : { kind: "piece", pieceId: owner.piece.id };
        this.ctx.setSelection({
          pieceId: null,
          decorationId: null,
          stitchingScope: scope,
        });
        return;
      }
      const piece = proj.pieces.find((p) => p.id === pieceId);
      const scope: StitchingScope = overrides
        ? {
            kind: "bucket",
            pieceId,
            lineIds: piece ? bucketmateLineIds(piece, line.id) : [line.id],
          }
        : piece?.stitchingDefaults
          ? { kind: "piece", pieceId }
          : { kind: "project" };
      this.ctx.setSelection({
        pieceId: null,
        decorationId: null,
        stitchingScope: scope,
      });
    });
    btnRow.appendChild(locateBtn);
    wrap.appendChild(btnRow);

    this.appendStitchMatchSection(wrap, pieceId, line, summary);
    this.appendStitchGridSection(wrap, line);
    this.appendStitchTraceRows(wrap, pieceId, line);

    // Advanced layout (#691) — also positional, so it commits without promoting
    // the line to a chisel override. A MATCHED seam's row is driven by its
    // partner's count rather than by the pitch, so the control is hidden there:
    // it would appear to do nothing. Last of the settings, just above the
    // promote button: it is the specialist corner of this panel, and everything
    // above it is what a seam needs every time.
    this.appendCornerLayoutFields(wrap, pieceId, line);

    // Promote to construction-backed (#740).
    if (
      isFeatureEnabled("construction-connections") &&
      !line.constructionOutline
    ) {
      const scope = this.ctx.scene.getPaperScope();
      wrap.appendChild(
        this.promoteButton("this stitch line", () =>
          this.ctx.commit(
            "Create construction line",
            (proj) =>
              promoteStitchToConstruction(proj, pieceId, line.id, scope) ??
              proj,
          ),
        ),
      );
    }

    this.root.appendChild(wrap);
  }

  /** Construction-stitch grid (#199 Phase 3): when this seam auto-aligns to a
   *  shared construction grid, show which seam LEADS the grid (defines the hole
   *  spacing + positions) and which FOLLOW it — a read-only Connections section
   *  mirroring the match UI. The leader is the longest seam of the group; its
   *  Start handle anchors the row. Nothing to detach here (emergent), so the
   *  rows have no × — change a seam's pitch / unlink it to leave the grid. */
  /** "Traces …" rows for a seam traced from construction lines (#1202), the
   *  same rows a traced part shows; × detaches it and keeps its shape. They join
   *  the match's Connections section when the seam has one, so the panel never
   *  shows two sections of the same name. */
  private appendStitchTraceRows(
    wrap: HTMLElement,
    pieceId: string,
    line: StitchLine,
  ) {
    const rows = subTraceRowSpecs(this.ctx.history.state, {
      kind: "stitch",
      pieceId,
      id: line.id,
    });
    if (rows.length === 0) return;
    let list = wrap.querySelector<HTMLUListElement>(
      '[data-section="connections"] ul.constraints-list',
    );
    if (!list) {
      const section = panelSection("Connections");
      markPaidSection(section, "construction-connections");
      list = document.createElement("ul");
      list.className = "constraints-list";
      section.appendChild(list);
      wrap.appendChild(section);
    }
    for (const spec of rows) list.appendChild(this.buildConnectionRow(spec));
  }

  private appendStitchGridSection(wrap: HTMLElement, line: StitchLine) {
    if (!isFeatureEnabled("stitch-matching")) return;
    // The leader carries `gridMain` (no `gridPhase`); followers carry `gridPhase`.
    if (line.gridPhase == null && line.gridMain !== true) return;
    const group = stitchGridGroupFor(this.ctx.history.state, line.id);
    if (!group || group.members.length < 2) return;

    const leaderRef = group.leader;
    const key = leaderRef ? leaderRef.stitchId : line.id;
    const isLeader = line.gridMain === true;
    const others = group.members.filter((m) => m.stitchId !== line.id);

    const rowSpecs: ConnectionRowSpec[] = isLeader
      ? others.map((m) =>
          stitchGridRowSpec(
            key,
            m,
            `Aligns ${this.stitchDisplayName(m)} to this grid`,
            true,
          ),
        )
      : [
          stitchGridRowSpec(
            key,
            leaderRef,
            `Aligned to ${
              leaderRef ? this.stitchDisplayName(leaderRef) : "the grid leader"
            }`,
            false,
          ),
        ];

    const summary = document.createElement("div");
    summary.className = "stitch-summary";
    summary.textContent = isLeader
      ? others.length === 1
        ? `Leads the shared hole grid · 1 seam aligns to this`
        : `Leads the shared hole grid · ${others.length} seams align to this`
      : `Holes follow ${
          leaderRef ? this.stitchDisplayName(leaderRef) : "the leader"
        }'s grid and settings`;
    wrap.appendChild(summary);

    const section = panelSection("Grid alignment");
    markPaidSection(section, "connected-stitch-lines");
    const list = document.createElement("ul");
    list.className = "constraints-list";
    for (const spec of rowSpecs)
      list.appendChild(this.buildConnectionRow(spec));
    section.appendChild(list);
    wrap.appendChild(section);
  }

  /** An inline, clickable stitch name that selects the referenced seam (#199) —
   *  used wherever a summary mentions another stitch (the main names its
   *  follower, the follower names the main it inherits its style from). */
  private stitchNameLink(ref: StitchRef, label: string): HTMLElement {
    const link = document.createElement("button");
    link.type = "button";
    link.className = "stitch-name-link";
    link.textContent = label;
    link.title = `Select “${label}”`;
    link.addEventListener("click", (e) => {
      e.preventDefault();
      e.stopPropagation();
      this.ctx.setSelection({
        pieceId: ref.pieceId,
        stitchId: ref.stitchId,
        decorationId: null,
      });
    });
    return link;
  }

  /** Stitch matching (#199), directional:
   *   - unmatched seam → a "Match a seam to this one…" button (this becomes the
   *     controlling MAIN);
   *   - MAIN seam → a "… follows this seam" note + Break (its own controls stay
   *     above);
   *   - FOLLOWER seam → a "Follows … · N holes" row + Break (its only controls).
   *  Gated on the `stitch-matching` flag. */
  private appendStitchMatchSection(
    wrap: HTMLElement,
    pieceId: string,
    line: StitchLine,
    /** When given (the MAIN panel), the main's "… follows this seam" summary is
     *  inserted right AFTER this node (the top hole-count summary) instead of at
     *  the bottom, so both summaries sit together under the name field. */
    summaryAnchor?: HTMLElement,
  ) {
    if (!isFeatureEnabled("stitch-matching")) return;
    const project = this.ctx.history.state;
    const match = matchForStitch(project, line.id);

    // "Match selected" (#199 path B): when two OR MORE seams are multi-selected
    // (shift / cmd-click) and the panel's lead is among them, offer a one-click
    // match — the FIRST-selected seam is the main and every other selected seam
    // follows it (one main, many followers). The lead drives the panel, so the
    // button appears here.
    const selIds = this.ctx.scene.getSelection().stitchIds ?? [];
    if (selIds.length >= 2 && selIds.includes(line.id)) {
      const mainId = selIds[0]!;
      const mainPiece = pieceForStitchLine(project, mainId);
      const followerIds = selIds.slice(1);
      if (mainPiece) {
        const mainName = this.stitchDisplayName({
          pieceId: mainPiece.id,
          stitchId: mainId,
        });
        const btn = document.createElement("button");
        btn.type = "button";
        btn.className = "btn";
        btn.style.width = "100%";
        btn.style.marginTop = "8px";
        btn.textContent =
          selIds.length === 2
            ? "Match selected"
            : `Match ${selIds.length} seams`;
        btn.title =
          followerIds.length === 1
            ? `Match the selected seams so they share a hole count (they follow ${mainName})`
            : `Match the ${followerIds.length} other selected seams to ${mainName} (they all follow the first-selected seam)`;
        markPaidFeature(btn, "connected-stitch-lines");
        btn.addEventListener("click", () => {
          this.ctx.commit("Match selected seams", (proj) => {
            const refFor = (id: string): StitchRef | null => {
              const pc = pieceForStitchLine(proj, id);
              return pc ? { pieceId: pc.id, stitchId: id } : null;
            };
            const main = refFor(mainId);
            if (!main) return proj;
            const followers = followerIds
              .map(refFor)
              .filter((r): r is StitchRef => r !== null);
            return matchFollowersToMain(proj, main, followers);
          });
          // Per the chosen behavior: after marking, select just the main.
          this.ctx.setSelection({
            pieceId: mainPiece.id,
            stitchId: mainId,
            decorationId: null,
          });
        });
        wrap.appendChild(btn);
        return;
      }
    }

    // A construction-grid follower already follows its leader (#1105); matching
    // it by count would pull it out of the grid, so don't offer to.
    if (!match && line.gridPhase != null) return;

    if (!match) {
      // This seam becomes the FOLLOWER: pick another seam for it to follow
      // (that one stays the controlling main). Styled like the "Override stitch
      // settings" toggle above it.
      const btn = document.createElement("button");
      btn.type = "button";
      btn.className = "btn";
      btn.style.width = "100%";
      btn.style.marginTop = "8px";
      btn.textContent = "Match to another seam";
      btn.title =
        "Make this seam follow another seam's stitch count (click that seam on the canvas)";
      markPaidFeature(btn, "connected-stitch-lines");
      btn.addEventListener("click", () => {
        this.host.armStitchMatch({ pieceId, stitchId: line.id });
      });
      wrap.appendChild(btn);
      return;
    }

    // A seam follows exactly one main, but a main can have MANY followers
    // (#199 path B), so gather every match this seam is in and render one
    // Connections row per relationship.
    const all = matchesForStitch(project, line.id);
    const asFollower = all.find((m) => m.follower.stitchId === line.id);
    const asMain = all.filter((m) => m.main.stitchId === line.id);

    // Connection rows: "Matched by <follower>" on the main (one per follower),
    // "Matches to <main>" on the follower.
    const rowSpecs = asFollower
      ? [
          (() => {
            const partner = stitchMatchPartner(asFollower, line.id);
            return stitchMatchRowSpec(
              asFollower.id,
              partner,
              this.stitchDisplayName(partner),
              false,
            );
          })(),
        ]
      : asMain.map((m) =>
          stitchMatchRowSpec(
            m.id,
            m.follower,
            this.stitchDisplayName(m.follower),
            true,
          ),
        );

    const row = document.createElement("div");
    row.className = "stitch-summary";
    if (!asFollower) {
      // Main: the shared count its follower(s) inherit. List how many follow.
      const n = this.retainedHoleCount(line, pieceId);
      const names = asMain.map((m) => this.stitchDisplayName(m.follower));
      if (names.length === 1) {
        // Single follower: report ITS actual punched count (fewer when it skips)
        // and, if it skips, the cadence — e.g. "Stitch 1 follows this seam,
        // skipping every 2nd stitch · 7 holes".
        const followerLine = project.pieces
          .flatMap((p) => p.stitchLines ?? [])
          .find((l) => l.id === asMain[0]!.follower.stitchId);
        // The follower's OWN retained count (on ITS piece, which may be smaller
        // than the main's) — a follower spilling past its part shows fewer holes
        // here too (#910 PR3).
        const fn = followerLine
          ? this.retainedHoleCount(followerLine, asMain[0]!.follower.pieceId)
          : n;
        const skip = followerLine
          ? effectiveSkipEvery(followerLine)
          : undefined;
        const skipClause =
          skip != null ? `, skipping every ${ordinal(skip)} hole` : "";
        row.appendChild(this.stitchNameLink(asMain[0]!.follower, names[0]!));
        row.appendChild(
          document.createTextNode(
            ` follows this seam${skipClause} · ${fn} hole${fn === 1 ? "" : "s"}`,
          ),
        );
      } else {
        row.textContent = `${names.length} seams follow this · ${n} holes`;
      }
      // Sit right under the top hole-count summary when an anchor is given;
      // otherwise fall back to the section's own position.
      if (summaryAnchor && summaryAnchor.parentElement === wrap) {
        wrap.insertBefore(row, summaryAnchor.nextSibling);
      } else {
        wrap.appendChild(row);
      }
    } else {
      const partnerName = this.stitchDisplayName(
        stitchMatchPartner(asFollower, line.id),
      );
      // Summary first — right under the name field: this seam's hole count +
      // spacing (updated live by the Skip stitches control below), with the
      // "Hole style & size follow …" note directly beneath it.
      row.textContent = this.followerSummaryText(
        line,
        pieceId,
        effectiveSkipEvery(line),
      );
      wrap.appendChild(row);

      const styleNote = document.createElement("div");
      styleNote.className = "stitch-summary";
      styleNote.appendChild(
        document.createTextNode("Hole style & size follow "),
      );
      styleNote.appendChild(
        this.stitchNameLink(
          stitchMatchPartner(asFollower, line.id),
          partnerName,
        ),
      );
      wrap.appendChild(styleNote);

      // Then the follower's own working-area heads. No start-of-path control —
      // a loop follower's rotation is slaved to its main's start (#692).
      // Refresh the "N holes · X mm pitch" summary as the heads are dragged, so
      // the follower's spacing updates live (it was frozen before). Read the
      // CURRENT line from the model — the panel doesn't rebuild on commit, so the
      // captured `line` misses a fill-mode / skip toggle — and apply the in-flight
      // insets on top.
      const refreshFollowerSummary = (startInset: number, endInset: number) => {
        const pc = this.ctx.history.state.pieces.find((p) => p.id === pieceId);
        const live =
          (pc && pieceStitchLines(pc).find((l) => l.id === line.id)) ?? line;
        const patched = { ...live, startInset, endInset };
        row.textContent = this.followerSummaryText(
          patched,
          pieceId,
          effectiveSkipEvery(patched),
        );
      };
      this.appendWorkingAreaField(
        wrap,
        pieceId,
        line,
        refreshFollowerSummary,
        false,
      );

      // "Match pitch exactly" (#199): ON (default) lays holes at the main's
      // pitch so equal-length seams line up hole-for-hole and the follower stays
      // put when the main's working area changes; OFF spreads the count evenly
      // to fill the follower's own working area. N/A on a continuous loop.
      if (!isStitchLoop(line)) {
        const matchPitch = checkbox(
          "Match pitch exactly",
          !line.fillWorkingArea,
          (checked) => {
            this.updateStitch(pieceId, line.id, (l) => {
              const { fillWorkingArea: _drop, ...restL } = l;
              return checked ? restL : { ...restL, fillWorkingArea: true };
            });
          },
        );
        // Match the Skip stitches control's left indent so the two align.
        matchPitch.classList.add("stitch-indent");
        wrap.appendChild(matchPitch);
      }

      // "Skip stitches" thins the row; sits right under "Match pitch exactly"
      // and live-updates the summary row above.
      this.appendFollowerSkipControl(wrap, pieceId, line, row);

      // Slant / diamond chisels have a direction the follower MAY override
      // (Regular / Inverse) while still following the main's shape + size.
      if (line.shape === "slant" || line.shape === "diamond") {
        wrap.appendChild(
          slantDirectionToggle(line.slantAngle, (deg) => {
            const reverse = deg >= 0;
            this.updateStitch(pieceId, line.id, (l) => ({
              ...l,
              slantReverse: reverse,
            }));
          }),
        );
      }
    }

    // The match is shown as a connection — a stitching-icon chip + a
    // Connections row whose × detaches it — instead of a bespoke "Break match"
    // button, so a matched seam reads like any other relationship (#199).
    const section = panelSection("Connections");
    markPaidSection(section, "connected-stitch-lines");
    const list = document.createElement("ul");
    list.className = "constraints-list";
    for (const spec of rowSpecs) {
      list.appendChild(this.buildConnectionRow(spec));
    }
    section.appendChild(list);
    wrap.appendChild(section);
  }

  /** The follower's match-summary line: "N holes · Y mm pitch", with the count
   *  + spacing reflecting a given "skip every Nth" value (#199). */
  private followerSummaryText(
    line: StitchLine,
    pieceId: string,
    skipEvery: number | undefined,
  ): string {
    // The COUNT reflects only the holes that actually land on THIS follower's
    // leather (#910 PR3): route through the shared on-piece retention with the
    // requested skip applied, so a follower whose matched row overflows its
    // (smaller) part shows FEWER holes than the inherited `holeCount` — and it
    // recomputes on every panel re-render / working-area drag / skip change
    // rather than reading the stale stored count. The SPACING stays the
    // follower's own along-seam pitch (2-decimal precision, matching
    // `stitchSummary`, so "3.85 mm" doesn't read as "3.9 mm").
    const patched = { ...line, skipStitches: skipEvery != null, skipEvery };
    const n = this.retainedHoleCount(patched, pieceId);
    const spacing = effectiveStitchSpacing(patched);
    return `${n} hole${n === 1 ? "" : "s"} · ${spacing.toFixed(2)} mm pitch`;
  }

  /** Follower-only "Skip stitches" control (#199): a toggle + an "every Nth"
   *  value, side by side. The value is disabled until the toggle is on, and
   *  required (defaults to 2) once it is; range `2 … floor(holeCount / 2)`. The
   *  `summaryRow` updates live so its hole count + spacing track the skip. */
  private appendFollowerSkipControl(
    wrap: HTMLElement,
    pieceId: string,
    line: StitchLine,
    summaryRow: HTMLElement,
  ) {
    const total = line.holeCount ?? 0;
    const max = maxSkipEvery(total); // 0 when the seam is too short to skip
    const canSkip = max >= 2;
    const clampN = (v: number) =>
      Math.min(Math.max(2, Math.round(v)), Math.max(2, max));

    const field = document.createElement("div");
    field.className = "field";

    const controls = document.createElement("div");
    controls.className = "skip-controls";

    // The checkbox carries its own inline "Skip Stitches" label (clicking the
    // text toggles it) plus an "ⓘ" link to the technique video, grouped tight;
    // the number field sits after a gap.
    const group = document.createElement("div");
    group.className = "skip-toggle-group";
    const toggleLabel = document.createElement("label");
    toggleLabel.className = "skip-toggle";
    const toggle = document.createElement("input");
    toggle.type = "checkbox";
    const toggleText = document.createElement("span");
    toggleText.textContent = "Skip Stitches";
    toggleLabel.appendChild(toggle);
    toggleLabel.appendChild(toggleText);
    const info = document.createElement("a");
    info.className = "skip-info";
    info.href = SKIP_STITCH_TECHNIQUE_URL;
    info.target = "_blank";
    info.rel = "noopener noreferrer";
    info.textContent = "ⓘ";
    info.title = "Watch the “skipping the stitch” technique";
    info.setAttribute(
      "aria-label",
      "Watch the skipping-the-stitch technique (opens a video in a new tab)",
    );
    group.appendChild(toggleLabel);
    group.appendChild(info);

    const input = document.createElement("input");
    input.type = "number";
    input.min = "2";
    input.max = String(Math.max(2, max));
    input.step = "1";
    input.setAttribute("aria-label", "Skip every Nth stitch");

    // The "Skip every Nth stitch" note sits directly under the number field
    // (aligned to it), not across the whole control line.
    const note = document.createElement("div");
    note.className = "stitch-summary";
    const numberCol = document.createElement("div");
    numberCol.className = "skip-number-col";
    numberCol.appendChild(input);
    numberCol.appendChild(note);

    controls.appendChild(group);
    controls.appendChild(numberCol);
    field.appendChild(controls);

    const enabled0 = effectiveSkipEvery(line) != null;
    // The number field carries a value only while skipping is ON. Unchecking
    // clears it; re-checking fills the freshly-calculated default.
    const shown0 = enabled0 ? clampN(line.skipEvery ?? 2) : undefined;
    input.value = shown0 != null ? String(shown0) : "";
    toggle.checked = enabled0;
    toggle.disabled = !canSkip;
    input.disabled = !canSkip || !enabled0;

    // Refresh the note + the live "N holes · X mm" summary for a given effective
    // skip (a number when skipping, or undefined when not).
    const describe = (skip: number | undefined) => {
      note.textContent = !canSkip
        ? "Seam is too short to skip stitches"
        : skip == null
          ? "Don't skip stitches"
          : `Skip every ${ordinal(skip)} hole`;
      summaryRow.textContent = this.followerSummaryText(line, pieceId, skip);
    };
    describe(shown0);

    // The default is derived from the follower's working area, which can change
    // (e.g. the user drags the start point, compressing the row) without this
    // control being rebuilt — so read the CURRENT line from the model when
    // re-checking, not the closure captured at build time, or the recalculated
    // default would be stale until the seam is re-selected.
    const liveLine = (): StitchLine => {
      const pc = this.ctx.history.state.pieces.find((p) => p.id === pieceId);
      return pc
        ? (pieceStitchLines(pc).find((l) => l.id === line.id) ?? line)
        : line;
    };

    const enable = (n: number) =>
      this.updateStitch(pieceId, line.id, (l) => ({
        ...l,
        skipStitches: true,
        skipEvery: n,
      }));
    // Off: drop the value entirely so re-enabling recalculates the default.
    const disable = () =>
      this.updateStitch(pieceId, line.id, (l) => {
        const { skipEvery: _drop, ...rest } = l;
        return { ...rest, skipStitches: false };
      });

    toggle.addEventListener("change", () => {
      const on = toggle.checked;
      input.disabled = !on;
      if (on) {
        const val = clampN(defaultSkipEvery(liveLine())); // recalc from current
        input.value = String(val);
        describe(val);
        enable(val);
        input.focus();
      } else {
        input.value = ""; // clear the field when unchecked
        describe(undefined);
        disable();
      }
    });
    // Live preview as the user types (only reachable while enabled).
    input.addEventListener("input", () => {
      const raw = Math.round(Number(input.value));
      describe(
        Number.isFinite(raw) && raw >= 2
          ? Math.min(raw, Math.max(2, max))
          : undefined,
      );
    });
    input.addEventListener("change", () => {
      const val = clampN(Number(input.value) || defaultSkipEvery(liveLine()));
      input.value = String(val);
      describe(val);
      enable(val);
    });

    wrap.appendChild(field); // note lives inside the field, under the number
  }

  /** Display name for a stitch line addressed by ref (mirrors the panel
   *  heading's "Stitch N" fallback). */
  private stitchDisplayName(ref: StitchRef): string {
    const piece = this.ctx.history.state.pieces.find(
      (p) => p.id === ref.pieceId,
    );
    if (!piece) return "another seam";
    const lines = pieceStitchLines(piece);
    const idx = lines.findIndex((l) => l.id === ref.stitchId);
    if (idx < 0) return "another seam";
    return lines[idx]!.name ?? `Stitch ${idx + 1}`;
  }

  /**
   * Generic stitching-defaults editor used by the stitch tool's tool-
   * properties (project block), by the part-level section when the matching
   * `stitchingScope` is selected, and by the per-bucket override editor
   * when an override bucket is selected in the Layers panel. Edits go
   * through the cascade helpers (project / piece) or write to the bucket's
   * lines directly.
   */
  public buildStitchingDefaultsBlock(
    heading: string,
    target:
      | "project"
      | { pieceId: string }
      | { pieceId: string; lineIds: string[] },
  ): HTMLElement {
    const project = this.ctx.history.state;
    const wrap = document.createElement("div");
    wrap.style.marginTop = "8px";
    const h = document.createElement("h3");
    h.textContent = heading;
    wrap.appendChild(h);

    const isProject = target === "project";
    const isBucket = !isProject && "lineIds" in target;
    const isPiece = !isProject && !isBucket;

    let effective: StitchingDefaults;
    let isPieceOverriding = false;
    let pieceRef: Piece | undefined;
    if (isProject) {
      effective = resolveProjectStitchingDefaults(project);
    } else {
      pieceRef = project.pieces.find((p) => p.id === target.pieceId);
      if (isBucket) {
        // Bucket settings come from the first matching line — every line in
        // the bucket shares the same override, so picking any of them is
        // fine. Falls back to the part defaults if the lineIds went stale.
        const lines = pieceRef ? pieceStitchLines(pieceRef) : [];
        const sample = lines.find((l) => target.lineIds.includes(l.id));
        if (sample) {
          effective = {
            shape: sample.shape,
            pitch: sample.pitch,
            size: sample.size,
            slantAngle: sample.slantAngle,
          };
        } else {
          effective = pieceRef
            ? resolvePartStitchingDefaults(project, pieceRef)
            : resolveProjectStitchingDefaults(project);
        }
      } else {
        isPieceOverriding = !!pieceRef?.stitchingDefaults;
        effective = pieceRef
          ? resolvePartStitchingDefaults(project, pieceRef)
          : resolveProjectStitchingDefaults(project);
      }
    }

    if (isPiece) {
      // Part-scope override is a single button that toggles between two
      // labels — same pattern as the per-line override on the stitch panel.
      // When off, no editor is rendered (the user opts in first); when on,
      // the editor below becomes the source of truth for this part.
      const btn = document.createElement("button");
      btn.type = "button";
      btn.className = "btn";
      btn.style.width = "100%";
      btn.textContent = isPieceOverriding
        ? "Stop overriding"
        : "Override project defaults";
      btn.addEventListener("click", () => {
        const proj = this.ctx.history.state;
        const next = isPieceOverriding ? undefined : { ...effective };
        this.ctx.commit(
          isPieceOverriding
            ? "Inherit project stitching"
            : "Override part stitching",
          () => setPartStitchingDefaults(proj, target.pieceId, next),
        );
        this.refresh();
      });
      wrap.appendChild(btn);
    }

    const editable = isProject || isBucket || isPieceOverriding;
    // For a piece scope that's still inheriting, the panel ends at the
    // override toggle — there's no point showing the read-only chisel /
    // pitch / size editor since it'd just mirror the project block. The
    // user clicks the toggle, then comes back for the editable controls.
    if (isPiece && !isPieceOverriding) return wrap;
    const onChange = (next: StitchingDefaults) => {
      const proj = this.ctx.history.state;
      if (isProject) {
        this.ctx.commit("Edit project stitching defaults", () =>
          setProjectStitchingDefaults(proj, next),
        );
      } else if (isBucket) {
        const ids = new Set(target.lineIds);
        this.ctx.commit("Edit stitch override", () => ({
          ...proj,
          pieces: proj.pieces.map((pc) =>
            pc.id !== target.pieceId
              ? pc
              : {
                  ...pc,
                  stitchLines: pieceStitchLines(pc).map((l) =>
                    ids.has(l.id)
                      ? {
                          ...l,
                          shape: next.shape,
                          pitch: next.pitch,
                          size: next.size,
                          slantAngle: next.slantAngle,
                          overridesDefaults: true,
                        }
                      : l,
                  ),
                },
          ),
        }));
      } else {
        this.ctx.commit("Edit part stitching defaults", () =>
          setPartStitchingDefaults(proj, target.pieceId, next),
        );
      }
      this.refresh();
    };

    const galleryWrap = stitchShapeGallery(effective.shape, (shape) => {
      if (!editable) return;
      // Reset size + pitch + pitchMode to the new chisel's natural
      // defaults — see the per-line gallery for the rationale.
      onChange({
        ...effective,
        shape,
        size: stitchSizeRange(shape).defaultValue,
        pitch: defaultPitchForShape(shape),
        pitchMode: defaultPitchModeForShape(shape),
      });
    });
    if (!editable) galleryWrap.classList.add("read-only-section");
    wrap.appendChild(galleryWrap);

    const pitchWrap = stitchPitchField(effective.pitch, {
      mode: effective.pitchMode ?? "center",
      onModeChange: (mode) => {
        if (!editable) return;
        onChange({ ...effective, pitchMode: mode });
      },
      onPitchChange: (v, modeOverride, fromPreset) => {
        if (!editable) return;
        // Auto-scale tooth width on preset clicks for slant + diamond
        // chisels — see the per-line gallery for the rationale.
        const autoTooth =
          fromPreset &&
          (effective.shape === "slant" || effective.shape === "diamond") &&
          isAutoToothForPitch(effective.size, effective.pitch);
        onChange({
          ...effective,
          pitch: v,
          ...(modeOverride !== undefined ? { pitchMode: modeOverride } : {}),
          ...(autoTooth ? { size: defaultToothWidthForPitch(v) } : {}),
        });
      },
      // Only offer the reset affordance when the editor is actually
      // editable — otherwise it'd flip the section into edit mode by
      // accident.
      ...(editable
        ? {
            onResetToDefaults: () => {
              onChange({
                ...effective,
                pitch: defaultPitchForShape(effective.shape),
                pitchMode: defaultPitchModeForShape(effective.shape),
                size: stitchSizeRange(effective.shape).defaultValue,
                slantAngle:
                  effective.shape === "slant" ? -45 : effective.slantAngle,
              });
            },
          }
        : {}),
    });
    if (!editable) pitchWrap.classList.add("read-only-section");
    wrap.appendChild(pitchWrap);

    const sizeRange = stitchSizeRange(effective.shape);
    const sizeWrap = lengthField(
      sizeRange.label,
      effective.size,
      sizeRange.min,
      sizeRange.max,
      sizeRange.step,
      (v) => {
        if (!editable) return;
        onChange({ ...effective, size: v });
      },
      { noSlider: true },
    );
    if (!editable) sizeWrap.classList.add("read-only-section");
    wrap.appendChild(sizeWrap);

    if (effective.shape === "slant" || effective.shape === "diamond") {
      const slantWrap = slantDirectionToggle(effective.slantAngle, (deg) => {
        if (!editable) return;
        onChange({ ...effective, slantAngle: deg });
      });
      if (!editable) slantWrap.classList.add("read-only-section");
      wrap.appendChild(slantWrap);
    }

    return wrap;
  }
}
